import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, CheckCircle2, Eye, Loader2, UserCheck, XCircle, FileText, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, absUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS, brl, fmtDate, maskCpf, maskPhone, validateCpf, onlyDigits, apiError, SPORT_LABEL } from "@/lib/format";

const emptyManual = { full_name: "", cpf: "", birth_date: "", gender: "M", whatsapp: "", email: "", shirt_size: "M", category_id: "", status: "PENDING_PAYMENT", payment_method: "PIX", accept_regulation: true };

export default function Participants() {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const can = (p) => isSuper || (user?.permissions || []).includes(p);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("ALL");
  const [sportF, setSportF] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [proof, setProof] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [waMsg, setWaMsg] = useState(null);
  const waLink = (phone, text) => { const d = (phone || "").replace(/\D/g, ""); return `https://wa.me/${d.startsWith("55") ? d : "55" + d}?text=${encodeURIComponent(text || "")}`; };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusF !== "ALL") params.status = statusF;
      if (sportF !== "ALL") params.sport_type = sportF;
      const { data } = await api.get("/registrations", { params });
      setRows(data.registrations);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, statusF, sportF]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/public/event").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);

  const validate = async (reg) => {
    try {
      const { data } = await api.patch(`/registrations/${reg.id}/validate`);
      const c = data.paid_confirmations || {};
      toast.success(`${reg.bib_code} validado como PAGO! E-mail: ${c.email === "sent" ? "enviado" : c.email === "skipped" ? "sem e-mail" : "falhou"}`);
      if (c.whatsapp !== "sent" && data.runner?.whatsapp) setWaMsg({ phone: data.runner.whatsapp, text: data.paid_message, bib: reg.bib_code });
      load();
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async (reg) => {
    try { await api.patch(`/registrations/${reg.id}/status`, { status: "CANCELLED" }); toast.success("Inscrição cancelada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-participants">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Operacional</p>
          <h1 className="font-display text-4xl font-black uppercase">Inscrições</h1>
        </div>
        {can("OPERATOR") && (
          <Button data-testid="manual-register-btn" onClick={() => setManualOpen(true)} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
            <Plus className="w-4 h-4 mr-1" /> Inscrição Manual
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            className="bg-surface border-white/10 pl-9" placeholder="Buscar por nome, CPF, peito ou status..." />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="filter-status" className="w-[190px] bg-surface border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white">
            <SelectItem value="ALL">Todos os status</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sportF} onValueChange={setSportF}>
          <SelectTrigger data-testid="filter-sport" className="w-[160px] bg-surface border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white">
            <SelectItem value="ALL">Todas modalidades</SelectItem>
            <SelectItem value="CORRIDA">Corrida</SelectItem>
            <SelectItem value="CICLISMO">Ciclismo</SelectItem>
            <SelectItem value="KIDS">Kids</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-surface border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                <th className="px-4 py-3">Peito</th>
                <th className="px-4 py-3">Atleta</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Modalidade</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-brand inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-slate-400">Nenhuma inscrição encontrada</td></tr>
              ) : rows.map((r) => {
                const st = STATUS[r.status] || {};
                return (
                  <tr key={r.id} data-testid={`participant-row-${r.bib_code}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 font-mono font-bold text-neon">{r.bib_code}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.runner?.full_name}</div>
                      <div className="text-xs text-slate-500">{r.runner?.gender === "M" ? "Masc." : "Fem."} • {fmtDate(r.runner?.birth_date)} {r.manual_registration && <span className="text-amber-300">• Manual{r.created_by?.name ? ` por ${r.created_by.name}` : ""} • {r.payment_method || "PIX"}</span>}{r.discount_percent ? <span className="text-emerald-300"> • {r.discount_percent}% desc.</span> : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300 font-mono text-xs">{maskCpf(r.runner?.cpf)}</td>
                    <td className="px-4 py-3 text-slate-300">{r.category?.name}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${st.bg}`}>{st.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.payment_proof_view_url && (
                          <Button data-testid={`view-proof-${r.bib_code}`} size="icon" variant="ghost" className="h-8 w-8 hover:bg-white/10"
                            onClick={() => setProof(r)}><Eye className="w-4 h-4" /></Button>
                        )}
                        {r.runner?.whatsapp && (
                          <a data-testid={`wa-${r.bib_code}`} href={waLink(r.runner.whatsapp, r.status === "PAID"
                            ? `1ª CEMOC RUN 2026 — ${r.runner.full_name.split(" ")[0]}, seu pagamento foi CONFIRMADO! ✅ Inscrição ${r.bib_code} (${r.category?.name}). Retire seu kit em 06/11 (08h–18h) no Ginásio Leonardo Barbosa de Moraes.`
                            : `1ª CEMOC RUN 2026 — ${r.runner.full_name.split(" ")[0]}, sua pré-inscrição ${r.bib_code} (${r.category?.name}) está aguardando pagamento de ${brl(r.category?.price)}. Pague pelo Portal do Atleta e envie o comprovante.`)}
                            target="_blank" rel="noopener noreferrer" title="Enviar WhatsApp"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#5EF08F] hover:bg-[#25D366]/15"><MessageCircle className="w-4 h-4" /></a>
                        )}
                        {r.status === "PAID" ? (
                          <span className="inline-flex items-center text-xs text-emerald-400 gap-1"><CheckCircle2 className="w-4 h-4" /></span>
                        ) : can("FINANCE") && r.status !== "CANCELLED" ? (
                          <Button data-testid={`validate-${r.bib_code}`} size="sm" className="h-8 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 font-bold"
                            onClick={() => validate(r)}><UserCheck className="w-4 h-4 mr-1" /> Validar</Button>
                        ) : null}
                        {can("OPERATOR") && r.status !== "CANCELLED" && (
                          <Button data-testid={`cancel-${r.bib_code}`} size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-500/10 text-red-400"
                            onClick={() => cancel(r)}><XCircle className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-500">{rows.length} inscrições listadas</p>

      {/* Proof modal */}
      <Dialog open={!!waMsg} onOpenChange={(o) => !o && setWaMsg(null)}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="wa-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Confirmar por WhatsApp</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300">Pagamento de <b className="text-neon">{waMsg?.bib}</b> validado. O e-mail já foi enviado — envie também a confirmação pelo WhatsApp do atleta:</p>
          <pre className="text-xs whitespace-pre-wrap bg-ink border border-white/10 rounded-lg p-3 text-slate-300">{waMsg?.text}</pre>
          <a data-testid="wa-confirm-send" href={waLink(waMsg?.phone, waMsg?.text)} target="_blank" rel="noopener noreferrer" onClick={() => setWaMsg(null)}
            className="inline-flex items-center justify-center h-11 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-ink font-bold uppercase tracking-wide">
            <Send className="w-4 h-4 mr-2" /> Abrir WhatsApp e enviar
          </a>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proof} onOpenChange={(o) => !o && setProof(null)}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="proof-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Comprovante — {proof?.bib_code}</DialogTitle></DialogHeader>
          {proof && (
            <div className="space-y-4">
              {proof.payment_proof_url?.toLowerCase().endsWith(".pdf") ? (
                <a href={absUrl(proof.payment_proof_view_url, true)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-brand underline"><FileText className="w-4 h-4" /> Abrir PDF do comprovante</a>
              ) : (
                <img src={absUrl(proof.payment_proof_view_url, true)} alt="Comprovante" className="w-full rounded-lg border border-white/10" />
              )}
              {can("FINANCE") && proof.status !== "PAID" && (
                <Button data-testid="proof-validate-btn" onClick={() => { validate(proof); setProof(null); }}
                  className="w-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 font-bold">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Validar Pagamento (marcar PAGO)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ManualDialog open={manualOpen} onOpenChange={setManualOpen} cats={cats} onDone={load} />
    </div>
  );
}

function ManualDialog({ open, onOpenChange, cats, onDone }) {
  const [form, setForm] = useState(emptyManual);
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (!open) { setForm(emptyManual); setEligible([]); } }, [open]);

  const refreshEligible = async (birth, gender) => {
    if (!birth) return;
    try {
      const { data } = await api.post("/public/categories/eligible", { birth_date: birth, gender });
      setEligible(data.categories);
      if (data.categories[0]) set("category_id", data.categories[0].id);
    } catch { setEligible([]); }
  };

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Informe o nome");
    if (!validateCpf(form.cpf)) return toast.error("CPF inválido");
    if (!form.category_id) return toast.error("Selecione a modalidade");
    setLoading(true);
    try {
      await api.post("/staff/registrations", { ...form, cpf: onlyDigits(form.cpf), whatsapp: form.whatsapp || null, email: form.email || null });
      toast.success("Inscrição manual criada!");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="manual-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Inscrição Manual</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300">Nome Completo *</Label>
            <Input data-testid="manual-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">CPF *</Label>
              <Input data-testid="manual-cpf" value={form.cpf} onChange={(e) => set("cpf", maskCpf(e.target.value))} className="bg-ink border-white/10 mt-1" placeholder="000.000.000-00" /></div>
            <div><Label className="text-slate-300">Nascimento *</Label>
              <Input data-testid="manual-birth" type="date" value={form.birth_date} onChange={(e) => { set("birth_date", e.target.value); refreshEligible(e.target.value, form.gender); }} className="bg-ink border-white/10 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">Sexo *</Label>
              <Select value={form.gender} onValueChange={(v) => { set("gender", v); refreshEligible(form.birth_date, v); }}>
                <SelectTrigger data-testid="manual-gender" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white"><SelectItem value="M">Masculino</SelectItem><SelectItem value="F">Feminino</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-slate-300">Camiseta</Label>
              <Select value={form.shirt_size} onValueChange={(v) => set("shirt_size", v)}>
                <SelectTrigger data-testid="manual-shirt" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">{["PP", "P", "M", "G", "GG"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-slate-300">Modalidade *</Label>
            <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger data-testid="manual-category" className="bg-ink border-white/10 mt-1"><SelectValue placeholder="Preencha nascimento/sexo" /></SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                {eligible.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {brl(c.price)}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">WhatsApp</Label>
              <Input data-testid="manual-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", maskPhone(e.target.value))} className="bg-ink border-white/10 mt-1" /></div>
            <div><Label className="text-slate-300">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="manual-status" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  <SelectItem value="PENDING_PAYMENT">Aguardando Pagamento</SelectItem>
                  <SelectItem value="PAID">Pago</SelectItem>
                  <SelectItem value="PRE_REGISTERED">Pré-inscrito</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="rounded-xl border border-white/10 bg-ink p-3 text-sm flex items-center justify-between">
            <span className="text-slate-300">Forma de recebimento</span>
            <span data-testid="manual-payment-method" className="font-bold text-neon">PIX (exclusivo)</span>
          </div>
          <Button data-testid="manual-submit-btn" onClick={submit} disabled={loading} className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Inscrição"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
